import moment from "moment";

import { Deserializable } from "../../shared/interfaces/deserialize";
import { ToolModel } from "./tool.model";
import { Constants } from "../../features/models/constants/_constants";
import { AmountPackModel } from "./amount-pack.model";
import { ManualModel } from "./manual.model";
import { QuotationModel } from "./quotation.model";

export class PurchasedPackModel implements Deserializable {
	_id!: string;
	id!: string;
	orderId!: string;
	client!: string;
	product!: ToolModel;
	packs!: Array<AmountPackModel>;
	status!: string;
	paymentMethod!: string;
	licences!: Array<any>;
	purchaseDate!: Date;
	renew!: boolean;
	renewalDate!: Date;
	active!: boolean;
	createdAt!: number;
	updatedAt!: number;
	isTrial!: boolean;
	timesRenewed!: number;
	core_renovation!: boolean;
	toCheck!: boolean;
	isAutoRenew?: Date;
	sale?: any;

	deserialize(obj: any) {
		Object.assign(this, obj);
		if (this.packs) {
			this.packs = this.packs.map((el) => new AmountPackModel(el));
		}
		return this;
	}

	public addPacks(...packs: AmountPackModel[]) {
		packs.forEach((pack) => {
			const existing_pack = this.packs.find((m_pack) => m_pack.isSamePack(pack));
			if (existing_pack !== undefined) {
				existing_pack.amount += pack.amount;
			} else {
				this.packs.push(pack);
			}
		});
	}

	public checkIfHasExpired(): boolean {
		if (this.renewalDate) {
			const currentDate = moment();
			const renewalDate = moment(this.renewalDate);
			return currentDate.isAfter(renewalDate);
		}
		return true;
	}

	public checkIfCanRenovate(): boolean {
		if (!this.renewalDate) return false;
		if (!this.licences || this.licences.length === 0) return false;

		const now = moment();
		const dateFifteenDayofRenewalDate = moment(this.renewalDate).add(15, "days");
		const dateThreeMonthsOfRenewalDate = moment(this.renewalDate).subtract(90, "days");
		const timeForRenewal = now.isBetween(dateThreeMonthsOfRenewalDate, dateFifteenDayofRenewalDate);

		if (this.status !== Constants.PURCHASED_PACK_STATUS.cancelled) {
			return timeForRenewal;
		}

		const core = this.packs?.some((pack) => pack.type == Constants.PACK_TYPE_NAMES.core);
		const sale = this.sale;

		if (!core) {
			return timeForRenewal;
		}

		if (!sale || (!sale.erpBill && !sale.paid)) {
			return false;
		}

		return timeForRenewal;
	}

	public checkIfSaleIsAutorenew(): boolean {
		if (this) {
			if (this.paymentMethod != Constants.PAYMENT_OPTIONS_NAMES.TRANSFER) {
				return false;
			}
			if (
				this["sale"].status === Constants.PURCHASED_PACK_STATUS.done &&
				this["sale"].paymentMethod === Constants.PAYMENT_OPTIONS_NAMES.TRANSFER &&
				this["sale"].paid === false &&
				this.renew &&
				this.licences.length === 1 &&
				this.paymentMethod === Constants.PAYMENT_OPTIONS_NAMES.TRANSFER &&
				this.status === Constants.PURCHASED_PACK_STATUS.done
			) {
				return true;
			}
			return false;
		}
		return false;
	}

	public checkIfInExpirationDays(): boolean {
		if (this.renewalDate) {
			const currentDate = moment();
			const renewalDate = moment(this.renewalDate).add(60, "days");
			return !currentDate.isAfter(renewalDate);
		}
		return false;
	}

	public checkIfInExpiration30Days(): boolean {
		if (this.renewalDate) {
			const currentDate = moment();
			const renewalDateMore30Days = moment(this.renewalDate).add(30, "days");
			return currentDate.isSameOrAfter(renewalDateMore30Days);
		}
		return false;
	}

	public checkCancelledStatus(): boolean {
		if (this.status) {
			return this.status === Constants.PURCHASED_PACK_STATUS.cancelled;
		}
		return false;
	}

	public getId(): string {
		return this._id;
	}

	public getProductName(): string {
		if (this.product instanceof ToolModel) {
			return this.product.getName() ? this.product.getName() : "";
		}
		return "";
	}

	public getCore(): boolean {
		for (const pack of this.packs) {
			if (pack.type == "core") {
				return true;
			}
		}
		return false;
	}

	public getProductDescription(language?: "es" | "en" | "fr" | "zh" | "it" | "pt" | "jp" | "tr"): string {
		if (this.product instanceof ToolModel) {
			return this.product.getShortDescription(language) ? this.product.getShortDescription(language) : "";
		}
		return "";
	}

	public getProductImage(): string {
		if (this.product instanceof ToolModel) {
			return this.product.getMainImage() ? this.product.getMainImage() : "";
		}
		return "";
	}

	public getProductManuals(): Array<ManualModel> {
		if (this.product instanceof ToolModel) {
			return this.product.getManuals() ? this.product.getManuals() : [];
		}
		return [];
	}

	public getProductQuotation(): Array<QuotationModel> {
		if (this.product instanceof ToolModel) {
			return this.product.getQuotation() ? this.product.getQuotation() : [];
		}
		return [];
	}

	public getStatus(): boolean {
		return this.active;
	}

	public getLicences(): string[] {
		return Array.isArray(this.licences) ? this.licences : [];
	}

	public getPrice(): number {
		let price = 0;
		for (const pack of this.packs) {
			let packPrice;
			if (this.licences.length > 0 && !this.core_renovation && !this.isTrial) {
				packPrice = pack.renewPrice;
			} else {
				packPrice = pack.price;
			}
			price += packPrice;
		}
		return price;
	}

	public getLicenceNumber() {
		let licenceNumber = 0;
		for (const pack of this.packs) {
			licenceNumber += Number(pack.licenceNumber);
		}

		return licenceNumber.toString();
	}

	public getSubscriptionUnit() {
		let subscriptionUnit;
		if (this.packs.length > 0) {
			subscriptionUnit = this.packs[0].subscriptionUnit;
		}
		return subscriptionUnit;
	}

	public getParsedPaymentMethod(): string {
		if (this.paymentMethod && this.paymentMethod.toUpperCase() in Constants.PAYMENT_OPTIONS_NAMES) {
			return Constants.PAYMENT_OPTIONS.filter((element: { title: string; id: string }) => {
				return element.id === this.paymentMethod;
			})[0].title;
		}
		return "---";
	}

	public getRenewStatus(): boolean {
		return this.renew;
	}

	public getLastPayment(): string {
		if (this.purchaseDate) {
			return moment(this.purchaseDate).format("LL");
		}
		return "---";
	}

	public getNextPayment(): string {
		if (this.renewalDate) {
			return moment(this.renewalDate).format("LL");
		}
		return "---";
	}

	public getPackName(): string {
		if (this.packs) {
			const names = this.packs.map((el) => el.name);
			return names.join(", ");
		}
		return "";
	}

	public hasSetups(): boolean {
		return Array.isArray(this.product.setups) && this.product.setups.length > 0;
	}

	public numberOfDaysToExpiration(): number {
		const oneDay = 24 * 60 * 60 * 1000; // hours*minutes*seconds*milliseconds
		const currentDate: any = new Date();
		const expirationDate: any = new Date(this.renewalDate);
		return Math.round(Math.abs((currentDate - expirationDate) / oneDay));
	}
}
